export interface ProviderErrorInfo {
  summary: string
  detail?: string
  status?: number
  code?: string
}

const HTTP_MESSAGES: Record<number, string> = {
  400: '请求参数有误，请检查配置',
  401: 'API Key 无效，请重新检查或到服务商后台重新获取',
  403: '没有访问权限，可能是账户被限流或未开通对应模型',
  404: '接口地址可能填错了，请确认 Base URL',
  408: '请求超时，请稍后重试',
  409: '请求冲突，请稍后重试',
  413: '请求体过大',
  422: '请求被拒绝，请检查配置合法性',
  429: '请求过于频繁或额度耗尽，请稍后再试',
  500: '服务商出了点问题，请稍后重试',
  502: '服务商网关异常，请稍后重试',
  503: '服务商暂不可用，请稍后重试',
  504: '服务商响应超时，请稍后重试',
}

function pick<T = unknown>(source: unknown, key: string): T | undefined {
  if (source && typeof source === 'object' && key in source)
    return (source as Record<string, unknown>)[key] as T
  return undefined
}

function tryParseJsonSegment(raw: string): unknown | undefined {
  const trimmed = raw.trim()
  if (!trimmed)
    return undefined
  // 整段先尝试
  try {
    return JSON.parse(trimmed)
  }
  catch {}
  // 大 body（如服务商把整个 HTML 错误页塞进 Error.message）：放弃回退扫描，避免 O(n²)
  if (trimmed.length > 50_000)
    return undefined
  // 从第一个 '{' 或 '[' 逐步向后缩，避免 "foo { a:1 } bar { b:2 }" 这种把两段拼起来 parse 失败
  const openIdx = [trimmed.indexOf('{'), trimmed.indexOf('[')].filter(i => i >= 0).sort((a, b) => a - b)[0]
  if (openIdx == null)
    return undefined
  const opener = trimmed[openIdx]
  const closer = opener === '{' ? '}' : ']'
  for (let end = trimmed.lastIndexOf(closer); end > openIdx; end = trimmed.lastIndexOf(closer, end - 1)) {
    try {
      return JSON.parse(trimmed.slice(openIdx, end + 1))
    }
    catch {}
  }
  return undefined
}

function normalizeRaw(raw: string): { parsed: unknown, detail: string } {
  const trimmed = raw.trim()
  if (!trimmed)
    return { parsed: undefined, detail: '' }

  const parsed = tryParseJsonSegment(trimmed)
  if (parsed !== undefined)
    return { parsed, detail: JSON.stringify(parsed, null, 2) }
  return { parsed: undefined, detail: trimmed }
}

function extractStatus(raw: string, parsed: unknown): number | undefined {
  const fromParsed = pick<number>(parsed, 'status')
    ?? pick<number>(pick(parsed, 'error'), 'status')
    ?? pick<number>(parsed, 'statusCode')
  if (typeof fromParsed === 'number' && fromParsed >= 100 && fromParsed < 600)
    return fromParsed

  // 仅在明确的上下文里抽取状态码，避免误把 token 计数、年份等 3 位数当 HTTP status
  const labeled = raw.match(/\b(?:status(?:\s*code)?|http)\s*(?:[:=]\s*)?(\d{3})\b/i)
    ?? raw.match(/\bhttp\/\d\.\d\s+(\d{3})\b/i)
  if (labeled) {
    const n = Number(labeled[1])
    if (n >= 100 && n < 600)
      return n
  }
  return undefined
}

function extractCode(parsed: unknown): string | undefined {
  const error = pick<unknown>(parsed, 'error')
  const code = pick<string>(error, 'code') ?? pick<string>(parsed, 'code')
  return typeof code === 'string' ? code : undefined
}

function extractRawMessage(parsed: unknown): string | undefined {
  const error = pick<unknown>(parsed, 'error')
  const message = pick<string>(error, 'message') ?? pick<string>(parsed, 'message')
  return typeof message === 'string' ? message : undefined
}

export function serializeProviderError(err: unknown): ProviderErrorInfo {
  if (err == null) {
    return { summary: '未知错误' }
  }

  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : String(err)
  const { parsed, detail } = normalizeRaw(raw)
  const status = extractStatus(raw, parsed)
  const code = extractCode(parsed)
  const backendMessage = extractRawMessage(parsed)

  let summary: string
  if (status && HTTP_MESSAGES[status])
    summary = HTTP_MESSAGES[status]
  else if (backendMessage)
    summary = backendMessage
  else if (raw)
    summary = raw
  else
    summary = '未知错误'

  return {
    summary,
    detail: detail || undefined,
    status,
    code,
  }
}
